import { useState, type ReactNode } from 'react';
import { Button, Tooltip } from '@arco-design/web-react';
import { IconDelete, IconDownload, IconEye } from '@arco-design/web-react/icon';
import { AttachmentPreviewModal } from './AttachmentPreviewModal';
import { standardFileIconAssets } from './assets';
import './standard-attachment.css';

export interface StandardAttachmentFile {
  readonly id?: string;
  readonly key?: string;
  readonly name: string;
  readonly sizeText?: string;
  readonly info?: ReactNode;
  readonly thumbnailUrl?: string;
  readonly previewUrl?: string;
}

export type StandardFileIconType = keyof typeof standardFileIconAssets;

export function standardAttachmentType(name: string): StandardFileIconType {
  const extension = name.split('.').pop()?.toLowerCase();
  if (extension === 'pdf') return 'pdf';
  if (['doc', 'docm', 'docx', 'dot', 'dotm', 'dotx', 'rtf', 'wps', 'wpt'].includes(extension ?? '')) return 'word';
  if (['csv', 'et', 'ett', 'xls', 'xlsb', 'xlsm', 'xlsx', 'xlt', 'xltm', 'xltx'].includes(extension ?? '')) return 'excel';
  if (['dps', 'dpt', 'pot', 'potm', 'potx', 'pps', 'ppsm', 'ppsx', 'ppt', 'pptm', 'pptx'].includes(extension ?? '')) return 'ppt';
  if (['dwg', 'dxf'].includes(extension ?? '')) return 'dwg';
  if (extension === 'sap') return 'sap';
  if (['glb', 'gltf'].includes(extension ?? '')) return 'glb';
  if (extension === 'ifc') return 'ifc';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(extension ?? '')) return 'image';
  return 'other';
}

export interface StandardAttachmentProps<T extends StandardAttachmentFile> {
  readonly files: readonly T[];
  readonly mode?: 'edit' | 'readonly';
  readonly variant?: 'boxed' | 'plain';
  readonly title?: ReactNode;
  readonly className?: string;
  readonly emptyText?: ReactNode;
  readonly headExtra?: ReactNode;
  readonly renderInfo?: (file: T) => ReactNode;
  readonly onPreview?: (file: T) => void;
  readonly onDownload?: (file: T) => void;
  readonly onDelete?: (file: T) => void;
}

export function StandardAttachment<T extends StandardAttachmentFile>({
  files,
  mode = 'readonly',
  variant = 'boxed',
  title,
  className,
  emptyText = '暂无附件',
  headExtra,
  renderInfo,
  onPreview,
  onDownload,
  onDelete,
}: StandardAttachmentProps<T>) {
  const [previewingFile, setPreviewingFile] = useState<T>();

  const preview = (file: T) => {
    if (onPreview) onPreview(file);
    else setPreviewingFile(file);
  };

  return <div className={[
    'ui-attachment-box', 'standard-attachment', `standard-attachment--${variant}`, className,
  ]
    .filter(Boolean).join(' ')}>
    {(title || headExtra) ? <div className="ui-attachment-box__head standard-attachment__head">
      <span>{title}</span>
      {headExtra ?? <span>{files.length} 个文件</span>}
    </div> : null}
    <div className="ui-attachment-box__list standard-attachment__list">
      {files.length === 0 ? <span className="standard-attachment__empty">{emptyText}</span> : null}
      {files.map(file => {
        const type = standardAttachmentType(file.name);
        const info = renderInfo?.(file) ?? file.info ?? file.sizeText;
        return <div className="ui-attachment" key={file.id ?? file.key ?? file.name}>
          <div className="ui-attachment__meta">
            <span className={`ui-file-icon ui-file-icon--${type}${file.thumbnailUrl
              ? ' ui-file-icon--thumbnail' : ''}`}>
              <img src={file.thumbnailUrl ?? standardFileIconAssets[type]} alt="" />
            </span>
            <span className="ui-attachment__meta-content">
              <span className="ui-attachment__name" title={file.name}>{file.name}</span>
              {info ? <span className="ui-attachment__info">{info}</span> : null}
            </span>
          </div>
          <span className="ui-attachment__actions">
            {(file.previewUrl || onPreview) ? <Tooltip content="预览">
              <Button aria-label={`预览 ${file.name}`} icon={<IconEye />} size="mini" type="text"
                onClick={() => preview(file)} />
            </Tooltip> : null}
            {onDownload ? <Tooltip content="下载">
              <Button aria-label={`下载 ${file.name}`} icon={<IconDownload />} size="mini" type="text"
                onClick={() => onDownload(file)} />
            </Tooltip> : null}
            {mode === 'edit' && onDelete ? <Tooltip content="删除">
              <Button aria-label={`删除 ${file.name}`} icon={<IconDelete />} size="mini"
                status="danger" type="text" onClick={() => onDelete(file)} />
            </Tooltip> : null}
          </span>
        </div>;
      })}
    </div>
    <AttachmentPreviewModal file={previewingFile} visible={Boolean(previewingFile)}
      onClose={() => setPreviewingFile(undefined)} />
  </div>;
}
